import {
  isCosBucketUrl,
  toDisplayMediaUrl,
  toProxyMediaUrl,
} from "@/lib/media-url";
import {
  isColorAdjustDefault,
  DEFAULT_COLOR_ADJUST,
  hasImageFilter,
  normalizeColorAdjust,
  type ColorAdjust,
} from "./color-adjust-core";
import {
  applyImagePictureToCanvas,
  blendPictureAdjustmentMasks,
} from "./image-picture-canvas";
import { applyImageRetouchToCanvas } from "./image-retouch-canvas";
import { colorAdjustWorker } from "./color-adjust-worker-client";

export * from "./color-adjust-core";

export async function applyColorAdjustToCanvas(
  canvas: HTMLCanvasElement,
  colorAdj?: ColorAdjust | null,
  signal?: AbortSignal,
): Promise<void> {
  signal?.throwIfAborted();
  if (isColorAdjustDefault(colorAdj)) {
    return;
  }

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas 2D context unavailable");
  }

  const normalized = normalizeColorAdjust(colorAdj);
  await applyImageRetouchToCanvas(canvas, normalized.retouch, signal);
  const pixelsOnly = {
    ...normalized,
    picture: undefined,
    retouch: undefined,
    adjustmentMasks: [],
    filter: normalized.adjustmentMasks.some((mask) => mask.enabled)
      ? undefined
      : normalized.filter,
  };
  if (!isColorAdjustDefault(pixelsOnly)) {
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    // Worker 会转移 buffer；局部调色需在转移前保留原像素，避免读到已 detached 的数据。
    const original = normalized.adjustmentMasks.some((mask) => mask.enabled)
      ? new ImageData(imageData.data.slice(), imageData.width, imageData.height)
      : null;
    const adjusted = await colorAdjustWorker.process(
      imageData,
      pixelsOnly,
      signal,
    );
    signal?.throwIfAborted();
    if (original)
      await blendPictureAdjustmentMasks(
        original,
        adjusted,
        normalized.adjustmentMasks,
        loadProcessableImage,
        signal,
      );
    context.putImageData(adjusted, 0, 0);
  }
  if (
    normalized.adjustmentMasks.some((mask) => mask.enabled) &&
    hasImageFilter(normalized.filter)
  ) {
    const filtered = await colorAdjustWorker.process(
      context.getImageData(0, 0, canvas.width, canvas.height),
      { ...DEFAULT_COLOR_ADJUST, filter: normalized.filter },
      signal,
    );
    signal?.throwIfAborted();
    context.putImageData(filtered, 0, 0);
  }
  await applyImagePictureToCanvas(
    canvas,
    normalized.picture,
    loadProcessableImage,
    signal,
    (pixels, chroma, abort) => colorAdjustWorker.process(pixels, undefined, abort, chroma),
  );
}

export async function loadProcessableImage(
  src: string,
  signal?: AbortSignal,
): Promise<{ image: HTMLImageElement; revoke: () => void }> {
  signal?.throwIfAborted();
  const objectUrls: string[] = [];
  const revoke = () => {
    objectUrls.forEach((url) => URL.revokeObjectURL(url));
  };

  const loadImage = (source: string, useCors: boolean) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      signal?.throwIfAborted();
      const image = new Image();
      if (useCors) {
        image.crossOrigin = "anonymous";
      }
      const cleanup = () => {
        image.onload = null;
        image.onerror = null;
        signal?.removeEventListener("abort", abort);
      };
      const abort = () => {
        cleanup();
        image.src = "";
        reject(signal?.reason);
      };
      image.onload = () => {
        cleanup();
        resolve(image);
      };
      image.onerror = () => {
        cleanup();
        reject(new Error("Failed to load image"));
      };
      signal?.addEventListener("abort", abort, { once: true });
      image.src = source;
    });

  const resolveFetchableImageUrl = (source: string): string | null => {
    if (/^(?:blob:|data:)/i.test(source)) {
      return null;
    }

    const displayUrl = toDisplayMediaUrl(source);
    if (!displayUrl) {
      return null;
    }

    if (
      isCosBucketUrl(displayUrl) ||
      toProxyMediaUrl(displayUrl) !== displayUrl
    ) {
      // Prefer direct delivery, then use the account-scoped media proxy when
      // CORS or an image CDN response prevents canvas pixel access.
      return displayUrl;
    }

    if (displayUrl.startsWith("/")) {
      return displayUrl;
    }

    if (typeof window !== "undefined") {
      try {
        const parsed = new URL(displayUrl, window.location.origin);
        if (parsed.origin === window.location.origin) {
          return `${parsed.pathname}${parsed.search}${parsed.hash}`;
        }
      } catch {
        return null;
      }
    }

    return null;
  };

  try {
    const fetchableUrl = resolveFetchableImageUrl(src);
    if (fetchableUrl) {
      const proxyUrl = toProxyMediaUrl(fetchableUrl);
      let response: Response;
      try {
        response = await fetch(fetchableUrl, { signal });
        if (!response.ok)
          throw new Error(`Failed to fetch image: HTTP ${response.status}`);
      } catch (directError) {
        signal?.throwIfAborted();
        if (proxyUrl === fetchableUrl) {
          throw directError;
        }
        response = await fetch(proxyUrl, { signal });
      }
      if (!response.ok) {
        throw new Error(`Failed to fetch image: HTTP ${response.status}`);
      }
      const blob = await response.blob();
      signal?.throwIfAborted();
      const objectUrl = URL.createObjectURL(blob);
      objectUrls.push(objectUrl);
      return { image: await loadImage(objectUrl, false), revoke };
    }

    return { image: await loadImage(src, true), revoke };
  } catch (error) {
    revoke();
    throw error instanceof Error ? error : new Error(String(error));
  }
}

export async function renderAdjustedImageToBlob(
  image: HTMLImageElement,
  colorAdj?: ColorAdjust | null,
  options?: { maxDimension?: number; signal?: AbortSignal },
): Promise<Blob> {
  options?.signal?.throwIfAborted();
  const canvas = document.createElement("canvas");
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const maxDimension = Math.max(0, options?.maxDimension ?? 0);
  const scale =
    maxDimension > 0
      ? Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight))
      : 1;
  canvas.width = Math.max(1, Math.round(sourceWidth * scale));
  canvas.height = Math.max(1, Math.round(sourceHeight * scale));
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas 2D context unavailable");
  }

  try {
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    await applyColorAdjustToCanvas(canvas, colorAdj, options?.signal);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (options?.signal?.aborted) {
          reject(options.signal.reason);
        } else if (!blob) {
          reject(new Error("Canvas toBlob failed"));
        } else {
          resolve(blob);
        }
      }, "image/png");
    });
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}
